import { proxy, ref } from 'valtio';
import { createWebsocketManager } from './websocket/WebsocketManager.js';
import { createSimulation } from './websocket/Simulation.js';
import { SIMULATION_API_VERSION } from './Utils/simulation_utils.js';
import { initialHistory, initialFSStatus } from './Constants/InitialSettings.js';
import { all_access_rights, none_access_rights } from './Constants/constants.js';
import { isZoned } from './Utils/utils.js';
import { UpdateAlarmLog } from './Utils/alarm_utils.js';
import { applyControllerApiVersion } from './Utils/controller_metadata.js';
import { readStoredSettingsDraft } from './Utils/settings_draft';

import _ from 'lodash';

function stateManager() {
  let controllerMetadataRefreshPromise = null;
  let controllerMetadataRequest = null;
  let controllerMetadataRetry = null;
  let controllerMetadataTimeout = null;
  let initialLoadErrorReported = false;
  let metadataRevision = 0;
  const liveApiVersions = new Map();
  return {
    controllers: [],

    // set to true when the API call with controller data is received. Does not mean that
    // all controller data via websocket is received
    controllers_loaded: false,
    simulation: null,

    // Browser preference only; controller permissions and recording stay server-owned.
    previewFeaturesEnabled: localStorage.getItem('preview_features_enabled') === 'true',
    setPreviewFeaturesEnabled(enabled) {
      if (enabled) localStorage.setItem('preview_features_enabled', 'true');
      else localStorage.removeItem('preview_features_enabled');
      store.previewFeaturesEnabled = enabled;
    },

    alarm_catalog_state: {
      entries: null,
      loading: false,
      error: false
    },

    alarms: {
      activeAlarms: [],
      alarmsLog: [],
      unreadAlarms: false,
      openAlarms: false
    },
    messages: {
      messages: [],
      unreadMessages: false
    },

    init(wasm, address, hooks) {
      if (this !== store) return;
      const session = this;
      store.address = address.websocket;
      store.api_url = address.api_url;
      store.wasm = undefined; // ref(wasm);
      store.hooks = hooks;
      store.wsManager?.suspend?.('reinitialize');
      store.wsManager = ref(createWebsocketManager(wasm));
      const wsManager = store.wsManager;

      const isFirefox = navigator.userAgent.includes('Firefox/');
      const openSocket = () => {
        if (session !== store || session.wsManager !== wsManager) return;
        console.log('[wss-conn] initialising ws-manager');
        wsManager.init(session);
      };

      if (isFirefox) {
        console.log('[wss-conn] createWebsocketManager: on firefox. waiting 200ms...');
        setTimeout(openSocket, 200);
      } else {
        console.log('[wss-conn] createWebsocketManager: not on firefox. connecting immediatly...');
        openSocket();
      }

      store.profile = JSON.parse(localStorage.getItem('profile') ?? '{}');
      store.editMode = false;
    },

    loadAlarmCatalog(apiUrl) {
      if (this !== store) return;
      const session = this;
      if (store.alarm_catalog_state.entries || store.alarm_catalog_state.loading) return;

      store.alarm_catalog_state.loading = true;
      store.alarm_catalog_state.error = false;
      return fetch(apiUrl)
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        })
        .then((entries) => {
          if (session !== store) return;
          store.alarm_catalog_state.entries = Object.fromEntries(
            entries.map((entry) => [Number(entry.id), entry])
          );
        })
        .catch((error) => {
          if (session !== store) return;
          console.error('[alarm-catalog] Could not load alarm catalog:', error);
          store.alarm_catalog_state.error = true;
        })
        .finally(() => {
          if (session === store) session.alarm_catalog_state.loading = false;
        });
    },

    getControllerById: function (id) {
      return this.controllers.find((c) => c.id === id);
    },

    getParent: (controller, zone_id) =>
      isZoned(controller) && zone_id
        ? controller.zones[(zone_id ?? 'zone_0').replace('zone_', '')]
        : controller,

    getControllerForTab: function (params) {
      let controller = this.getControllerById(params.id);
      return {
        controller: controller,
        parent: this.getParent(controller, params.zone_id)
      };
    },

    getControllerByMacAddress: function (mac_address) {
      return store.controllers.find((c) => c.mac_address === mac_address);
    },

    getControllerByName: (name) => store.controllers.find((c) => c.name === name),

    addController: function (data, user, access_rights_per_controller) {
      // A response from a signed-out session must not populate a replacement store.
      if (this !== store) return;
      const { alarm_log, ...controllerData } = data;
      const temp_settings = readStoredSettingsDraft(data);
      let controller = {
        ...controllerData,
        zone_id: '254', // controller has global id: (0xFE/254)

        settings: {},
        temp_settings: temp_settings ? _.cloneDeep(temp_settings) : {}, // hold settings which are not saved yet
        /* wrap history in `data` is needed to keep valtio's `subscribe`
				   working when the history is replaced. Happens in tabs/Graph.js */
        history: { data: _.cloneDeep(initialHistory) },
        infos: { humidity_graph: [], temp_graph: [] },
        fs_status: _.cloneDeep(initialFSStatus),
        connection: {
          initialized: false,
          requested_all: false,
          request_values_full: false,
          info_graphs_received: false,
          info_graphs_received_at: 0,
          info_graphs_requested_at: 0,
          info_graphs_refresh_jitter_ms: null,
          status: 'bad',
          last_received: 0
        },
        skipped_values: {},
        access_rights:
          access_rights_per_controller === 'all'
            ? all_access_rights
            : access_rights_per_controller === 'none'
              ? none_access_rights
              : (access_rights_per_controller[data.mac_address] ?? {})
      };
      applyControllerApiVersion(controller, liveApiVersions.get(controller.id));
      // Update project_id
      controller.stable_project_id = controller.project_id;
      if (sessionStorage.getItem(controller.id + '_removed_project_restrictions')) {
        controller.project_id = 0;
      }

      // Update graph show/hide values from stored data.
      for (let [key, value] of Object.entries(data['graph_hidden'] ?? {})) {
        controller.history.data.hidden[key] = value;
      }
      delete controller['graph_hidden'];

      if (controller.type === 6000 || controller.type === 5155) {
        controller.zones = {};
        controller.slaves = [];
        controller.zoned = true;
      }

      if (controller.simulation === true) {
        // Persisted simulation records may still report the API version used when they were
        // created. The browser-side simulator always emits the current simulation protocol.
        controller.api_version = SIMULATION_API_VERSION;
        const activeSimulation = store.simulation;
        const activeSocketState = activeSimulation?.socket?.readyState;
        const activeSimulationCanBeReused =
          activeSimulation?.controller?.id === controller.id &&
          (activeSocketState === 0 || activeSocketState === 1);
        if (activeSimulationCanBeReused) {
          activeSimulation.controller = controller;
          controller.simulation = activeSimulation;
        } else {
          activeSimulation?.stop?.();
          controller.simulation = createSimulation(store.address, controller, user, store.wasm);
          store.simulation = ref(controller.simulation);
        }
      }

      // Mutate in place: replacing the array would detach every component
      // already subscribed to `store.controllers`.
      for (let i = store.controllers.length - 1; i >= 0; i--) {
        if (store.controllers[i].id === controller.id) store.controllers.splice(i, 1); // remove duplicates
      }
      store.controllers.push(controller);

      const registeredController = store.controllers.find((c) => c.id === controller.id);
      store.alarms.alarmsLog = UpdateAlarmLog(
        store.alarms.alarmsLog,
        alarm_log,
        registeredController
      );
    },

    recordControllerApiVersion(id, version) {
      if (this !== store || String(version ?? '').trim() === '') return;
      const controller = this.getControllerById(id);
      if (liveApiVersions.get(id) !== version || (controller && controller.api_version !== version)) {
        metadataRevision += 1;
      }
      // Registration can race the initial /api/data response.
      liveApiVersions.set(id, version);
      applyControllerApiVersion(controller, version);
    },

    refreshControllerMetadata: function ({ invalidate = false } = {}) {
      if (this !== store) return Promise.resolve(false);
      const session = this;
      if (invalidate) metadataRevision += 1;
      if (!store.api_url || (store.controllers.length === 0 &&
          (store.controllers_loaded || !store.hooks?.onControllerDataLoaded))) return Promise.resolve(false);
      if (controllerMetadataRefreshPromise) return controllerMetadataRefreshPromise;
      clearTimeout(controllerMetadataRetry);
      controllerMetadataRetry = null;
      const request = new AbortController();
      controllerMetadataRequest = request;
      const timeout = setTimeout(() => request.abort(), 10000);
      controllerMetadataTimeout = timeout;
      const retryInitialData = () => {
        controllerMetadataRetry = null;
        session.refreshControllerMetadata();
      };

      const refresh = async () => {
        let revision;
        do {
          revision = metadataRevision;
          try {
            const response = await fetch(session.api_url('/api/data'), {
              cache: 'no-store', signal: request.signal
            });
            if (request.signal.aborted || session !== store) return false;
            if (response.status === 401 || response.status === 403) {
              session.hooks?.onAuthenticationFailed?.();
              return false;
            }
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            if (request.signal.aborted || session !== store) return false;
            // A reconnect or live version notification supersedes this response.
            // Drain those notifications with one fresh request before resolving.
            if (revision !== metadataRevision) continue;
            if (!session.controllers_loaded && session.hooks?.onControllerDataLoaded) {
              session.hooks.onControllerDataLoaded(data);
              session.controllers_loaded = true;
              initialLoadErrorReported = false;
              return true;
            }
            for (const metadata of data.controller ?? []) {
              const controller = session.getControllerById(metadata.id);
              if (!controller) continue;
              applyControllerApiVersion(controller, metadata.api_version);
              if (metadata.name != null) controller.name = metadata.name;

              if (metadata.project_id != null) {
                const projectId = Number(metadata.project_id);
                if (Number.isInteger(projectId)) {
                  controller.stable_project_id = projectId;
                  if (!sessionStorage.getItem(`${controller.id}_removed_project_restrictions`)) {
                    controller.project_id = projectId;
                  }
                }
              }
            }
            return true;
          } catch (error) {
            if (controllerMetadataRequest !== request || session !== store) return false;
            console.warn('[controller-metadata] refresh failed', error);
            if (revision === metadataRevision) {
              if (!session.controllers_loaded && session.hooks?.onControllerDataLoaded) {
                if (!initialLoadErrorReported) {
                  session.hooks.enqueueSnackbar?.(error.message, { variant: 'error' });
                  initialLoadErrorReported = true;
                }
                controllerMetadataRetry = setTimeout(retryInitialData, 5000);
              }
              return false;
            }
          }
        } while (session === store && revision !== metadataRevision);
        return false;
      };
      const pending = refresh().finally(() => {
        clearTimeout(timeout);
        if (controllerMetadataTimeout === timeout) controllerMetadataTimeout = null;
        if (controllerMetadataRefreshPromise === pending) controllerMetadataRefreshPromise = null;
        if (controllerMetadataRequest === request) controllerMetadataRequest = null;
      });
      controllerMetadataRefreshPromise = pending;
      return pending;
    },

    cancelControllerMetadataRefresh() {
      clearTimeout(controllerMetadataTimeout);
      controllerMetadataTimeout = null;
      clearTimeout(controllerMetadataRetry);
      controllerMetadataRetry = null;
      controllerMetadataRequest?.abort();
      controllerMetadataRequest = null;
      controllerMetadataRefreshPromise = null;
    },

    /*
     * @param parent: reference to controller or to zone
     */
    saveSettings(controller, parent, newSettings, tab_id, transform = null) {
      if (newSettings !== null) {
        if (controller.connection?.status === 'good') {
          const settings_data = {};
          settings_data[tab_id] = transform
            ? transform(_.cloneDeep(newSettings))
            : _.cloneDeep(newSettings);
          const zoneable = controller.type === 6000 || controller.type === 5155;
          if (zoneable) {
            settings_data.zone = parent.zone_id;
          }
          const forward = controller.type === 6000; // server forwards without parsing data
          const zoned = zoneable;

          // This confirms submission to the socket, not controller acceptance.
          try {
            if (
              !store.wsManager.sendWSMsg(
                controller,
                'update_setpoints',
                settings_data,
                forward,
                zoned
              )
            )
              return false;
          } catch (error) {
            console.error('[settings] Failed to submit settings', error);
            return false;
          }
          parent.settings[tab_id] = _.cloneDeep(newSettings);
          return true;
        }
      }
      return false;
    },

    toggleCustomComponentVisibility: function (controller, parent, id, defaultVisible = true) {
      if (!store.profile[controller.id]) {
        store.profile[controller.id] = {};
      }
      if (!store.profile[controller.id][parent.id]) {
        store.profile[controller.id][parent.id] = {};
      }

      store.profile[controller.id][parent.id][id] = !(
        store.profile[controller.id][parent.id][id] ?? defaultVisible
      );
      localStorage.setItem('profile', JSON.stringify(store.profile));
    },

    // return reference to the real store behind useSnapshot().
    // use this for manipulating states directly without a set-function
    store() {
      return store;
    }
  };
}

let store;

export default function getStateManager() {
  if (store == null) store = proxy(stateManager()); // makes proxy re-initializable, used for logout
  return store;
}

export function destroyState() {
  store?.cancelControllerMetadataRefresh();
  store = null;
}
